import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { getStorefrontBootstrap } from "@/lib/storefront-bootstrap";
import { geoGuess } from "./api/geo";

export type CurrencyCode =
  | "USD"
  | "EUR"
  | "MXN"
  | "CAD"
  | "CHF"
  | "CUP"
  | "CLA"
  | "MLC"
  | "ZELLE";

export const FALLBACK_SELECTABLE_CODES: CurrencyCode[] = [
  "CUP",
  "USD",
  "EUR",
  "MXN",
  "CAD",
  "CHF",
  "CLA",
  "MLC",
  "ZELLE",
];

/** Alias for legacy callers expecting `SELECTABLE_CODES` import. */
export const SELECTABLE_CODES = FALLBACK_SELECTABLE_CODES;

type Rates = Partial<Record<CurrencyCode, number>>; // X -> CUP

type BootstrapPayload = {
  organization?: {
    currency?: string | null;
    enabledDisplayCurrencies?: string[] | null;
  };
  storefront?: {
    currency?: string | null;
    enabledDisplayCurrencies?: string[] | null;
  };
  rates?: Record<string, unknown>;
  exchangeRates?: {
    rates?: Record<string, unknown>;
    items?: Array<{ code?: string; rate?: unknown }>;
    asOf?: string | null;
  };
  asOf?: string | null;
};

type Ctx = {
  currency: CurrencyCode;
  setCurrency: (c: CurrencyCode) => void;

  storeCurrency: CurrencyCode;
  enabledCurrencies: CurrencyCode[];

  rates: Rates;
  asOf?: string | null;

  canConvert: (from: CurrencyCode, to: CurrencyCode) => boolean;
  convert: (cents: number, from: CurrencyCode, to: CurrencyCode) => number;
  fmt: (cents: number, currency: CurrencyCode) => string;

  ready: boolean;
};

const CurrencyCtx = createContext<Ctx | undefined>(undefined);
const STORAGE_KEY = "kwad.storefront.currency";

const NON_ISO_FALLBACK = new Set<CurrencyCode>(["CLA", "MLC", "ZELLE"]);

const LOCALE_BY_CURR: Record<CurrencyCode, string> = {
  USD: "en-US",
  EUR: "es-ES",
  MXN: "es-MX",
  CAD: "en-CA",
  CHF: "de-CH",
  CUP: "es-CU",
  CLA: "es-ES",
  MLC: "es-CU",
  ZELLE: "en-US",
};

function isSupportedCurrency(value: string): value is CurrencyCode {
  return (FALLBACK_SELECTABLE_CODES as string[]).includes(value);
}

function normalizeCurrency(
  value: unknown,
  fallback: CurrencyCode,
): CurrencyCode {
  const normalized = String(value ?? "")
    .trim()
    .toUpperCase();

  return isSupportedCurrency(normalized) ? normalized : fallback;
}

function normalizeCurrencyList(
  values: unknown,
  fallback: CurrencyCode[],
): CurrencyCode[] {
  if (!Array.isArray(values)) return fallback;

  const items = values
    .map((item) =>
      String(item ?? "")
        .trim()
        .toUpperCase(),
    )
    .filter((item): item is CurrencyCode => isSupportedCurrency(item));

  return items.length ? Array.from(new Set(items)) : fallback;
}

function toNumberLoose(input: unknown): number | null {
  const raw = String(input ?? "")
    .replace(/[^\d.,-]/g, "")
    .trim();

  if (!raw) return null;

  if (raw.includes(".") && raw.includes(",")) {
    const normalized = raw.replace(/\./g, "").replace(",", ".");
    const n = Number(normalized);
    return Number.isFinite(n) ? n : null;
  }

  if (raw.includes(",") && !raw.includes(".")) {
    const normalized = raw.replace(",", ".");
    const n = Number(normalized);
    return Number.isFinite(n) ? n : null;
  }

  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

function extractRatesFromBootstrap(data: BootstrapPayload): {
  rates: Rates;
  asOf: string | null;
} {
  const rates: Rates = { CUP: 1 };

  const recordCandidates = [data?.rates, data?.exchangeRates?.rates].filter(
    Boolean,
  ) as Array<Record<string, unknown>>;

  for (const candidate of recordCandidates) {
    for (const [key, value] of Object.entries(candidate)) {
      const code = normalizeCurrency(key, "CUP");
      const rate = toNumberLoose(value);
      if (rate && rate > 0) {
        rates[code] = rate;
      }
    }
  }

  const itemCandidates = data?.exchangeRates?.items ?? [];
  if (Array.isArray(itemCandidates)) {
    for (const item of itemCandidates) {
      const code = normalizeCurrency(item.code, "CUP");
      const rate = toNumberLoose(item.rate);
      if (rate && rate > 0) {
        rates[code] = rate;
      }
    }
  }

  return {
    rates,
    asOf: data?.exchangeRates?.asOf ?? data?.asOf ?? null,
  };
}

async function fetchStorefrontBootstrap(): Promise<{
  storeCurrency: CurrencyCode;
  enabledCurrencies: CurrencyCode[];
  rates: Rates;
  asOf: string | null;
}> {
  const { data } = await getStorefrontBootstrap();
  const payload = ((data as { data?: BootstrapPayload })?.data ??
    data) as BootstrapPayload;

  const storeCurrency = normalizeCurrency(
    payload?.organization?.currency ?? payload?.storefront?.currency,
    "CUP",
  );

  const enabledCurrencies = normalizeCurrencyList(
    payload?.organization?.enabledDisplayCurrencies ??
      payload?.storefront?.enabledDisplayCurrencies,
    [storeCurrency],
  );

  const fx = extractRatesFromBootstrap(payload);

  return {
    storeCurrency,
    enabledCurrencies: enabledCurrencies.includes(storeCurrency)
      ? enabledCurrencies
      : [storeCurrency, ...enabledCurrencies],
    rates: fx.rates,
    asOf: fx.asOf,
  };
}

function getSavedCurrency(): CurrencyCode | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;

    const value = stored.trim().toUpperCase();
    return isSupportedCurrency(value) ? value : null;
  } catch {
    return null;
  }
}

export function CurrencyProvider({ children }: { children: React.ReactNode }) {
  const [currency, setCurrencyState] = useState<CurrencyCode>("CUP");
  const [storeCurrency, setStoreCurrency] = useState<CurrencyCode>("CUP");
  const [enabledCurrencies, setEnabledCurrencies] = useState<CurrencyCode[]>([
    "CUP",
  ]);
  const [rates, setRates] = useState<Rates>({ CUP: 1 });
  const [asOf, setAsOf] = useState<string | null>(null);
  const [ready, setReady] = useState(false);

  const setCurrency = useCallback(
    (next: CurrencyCode) => {
      const normalized = normalizeCurrency(next, storeCurrency);
      const effective = enabledCurrencies.includes(normalized)
        ? normalized
        : storeCurrency;

      setCurrencyState(effective);

      try {
        localStorage.setItem(STORAGE_KEY, effective);
      } catch {
        // no-op
      }
    },
    [enabledCurrencies, storeCurrency],
  );

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const [bootstrap, geo] = await Promise.allSettled([
          fetchStorefrontBootstrap(),
          geoGuess(),
        ]);

        if (cancelled) return;

        const bootstrapValue =
          bootstrap.status === "fulfilled"
            ? bootstrap.value
            : {
                storeCurrency: "CUP" as CurrencyCode,
                enabledCurrencies: ["CUP"] as CurrencyCode[],
                rates: { CUP: 1 } as Rates,
                asOf: null,
              };

        const saved = getSavedCurrency();
        const geoCurrency =
          geo.status === "fulfilled" ? geo.value.currency : null;

        setStoreCurrency(bootstrapValue.storeCurrency);
        setEnabledCurrencies(bootstrapValue.enabledCurrencies);
        setRates({ CUP: 1, ...bootstrapValue.rates });
        setAsOf(bootstrapValue.asOf);

        const preferred =
          (saved && bootstrapValue.enabledCurrencies.includes(saved)
            ? saved
            : null) ??
          (geoCurrency && bootstrapValue.enabledCurrencies.includes(geoCurrency)
            ? geoCurrency
            : null) ??
          bootstrapValue.storeCurrency;

        setCurrencyState(preferred);
      } finally {
        if (!cancelled) setReady(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const canConvert = useCallback(
    (from: CurrencyCode, to: CurrencyCode) => {
      if (from === to) return true;

      const rFrom = from === "CUP" ? 1 : (rates[from] ?? 0);
      const rTo = to === "CUP" ? 1 : (rates[to] ?? 0);

      return rFrom > 0 && rTo > 0;
    },
    [rates],
  );

  const convert = useCallback(
    (cents: number, from: CurrencyCode, to: CurrencyCode) => {
      if (!Number.isFinite(cents)) return 0;
      if (from === to) return Math.round(cents);

      const rFrom = from === "CUP" ? 1 : (rates[from] ?? 0);
      const rTo = to === "CUP" ? 1 : (rates[to] ?? 0);

      if (!(rFrom > 0) || !(rTo > 0)) {
        return Math.round(cents);
      }

      const unitsFrom = cents / 100;
      const inCUP = unitsFrom * rFrom;
      const unitsTo = inCUP / rTo;
      return Math.round(unitsTo * 100);
    },
    [rates],
  );

  const fmt = useCallback((cents: number, curr: CurrencyCode) => {
    const amount = Math.max(0, cents) / 100;

    if (curr === "CUP") {
      return (
        amount.toLocaleString(LOCALE_BY_CURR.CUP, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }) + " MN"
      );
    }

    if (NON_ISO_FALLBACK.has(curr)) {
      return (
        amount.toLocaleString(LOCALE_BY_CURR[curr], {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }) + ` ${curr}`
      );
    }

    const locale =
      LOCALE_BY_CURR[curr] ||
      (typeof navigator !== "undefined" ? navigator.language : "en-US");

    try {
      return new Intl.NumberFormat(locale, {
        style: "currency",
        currency: curr,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);
    } catch {
      return (
        amount.toLocaleString(locale, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }) + ` ${curr}`
      );
    }
  }, []);

  const value = useMemo<Ctx>(
    () => ({
      currency,
      setCurrency,
      storeCurrency,
      enabledCurrencies,
      rates,
      asOf,
      canConvert,
      convert,
      fmt,
      ready,
    }),
    [
      currency,
      setCurrency,
      storeCurrency,
      enabledCurrencies,
      rates,
      asOf,
      canConvert,
      convert,
      fmt,
      ready,
    ],
  );

  return <CurrencyCtx.Provider value={value}>{children}</CurrencyCtx.Provider>;
}

export function useCurrency() {
  const ctx = useContext(CurrencyCtx);
  if (!ctx) {
    throw new Error("useCurrency must be used within CurrencyProvider");
  }
  return ctx;
}
